<template>
  <AppShell
    active="opportunities"
    :workspace="workspace"
    :title="opportunity.name"
    :subtitle="`${opportunity.market} / ${opportunity.county}, ${opportunity.state}`"
    max-width-class="max-w-none"
    data-testid="opportunity-detail-page"
  >
    <template #actions>
      <a href="/opportunities" class="inline-flex h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
        <ArrowLeft class="size-4" aria-hidden="true" />
        Pipeline
      </a>
      <button
        type="button"
        data-testid="favorite-opportunity-button"
        class="inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition disabled:cursor-wait disabled:opacity-60"
        :class="opportunity.favorited ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted hover:text-amber-600'"
        :aria-label="opportunity.favorited ? `Remove ${opportunity.name} from favorites` : `Add ${opportunity.name} to favorites`"
        :disabled="isFavoriting"
        @click="toggleFavorite"
      >
        <LoaderCircle v-if="isFavoriting" class="size-4 animate-spin" aria-hidden="true" />
        <Star v-else class="size-4" :class="opportunity.favorited ? 'fill-current' : ''" aria-hidden="true" />
        {{ opportunity.favorited ? 'Saved' : 'Favorite' }}
      </button>
      <a :href="mapOpportunityPath" data-testid="view-opportunity-on-map" class="hidden h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover sm:inline-flex">
        <MapPinned class="size-4" aria-hidden="true" />
        Map
      </a>
      <button v-if="!isEditing" type="button" data-testid="edit-opportunity-button" class="inline-flex h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover" @click="startEditing">
        <Pencil class="size-4" aria-hidden="true" />
        Edit
      </button>
      <a v-if="clickup.linked" :href="clickup.url" target="_blank" rel="noreferrer" class="hidden h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted sm:inline-flex">
        <ExternalLink class="size-4" aria-hidden="true" />
        ClickUp
      </a>
      <button type="button" data-testid="clickup-opportunity-sync" class="inline-flex h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-60" :disabled="isSyncing || !workspace.clickupEnabled" @click="syncOpportunity">
        <LoaderCircle v-if="isSyncing" class="size-4 animate-spin" aria-hidden="true" />
        <RefreshCw v-else class="size-4" aria-hidden="true" />
        Sync
      </button>
      <button type="button" data-testid="open-screening-drawer" class="inline-flex h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" @click="openScreeningDrawer">
        <RefreshCw class="size-4" aria-hidden="true" />
        Screening
      </button>
    </template>

    <div v-if="notice" class="mb-4 rounded-md border border-land-border bg-land-primary-soft px-4 py-3 text-sm font-medium text-land-primary" role="status">{{ notice }}</div>
    <div v-if="errors.length > 0" class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
      <p v-for="error in errors" :key="error">{{ error }}</p>
    </div>

    <section class="mb-4 rounded-md border border-land-border bg-land-surface shadow-sm" data-testid="opportunity-health-panel">
      <div class="grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_260px]">
        <div>
          <div class="flex flex-wrap items-center gap-2">
            <StateBadge :label="health.stateLabel || 'Unknown'" :tone="health.tone || 'slate'" />
            <StateBadge :label="opportunity.stageLabel || 'Stage'" tone="blue" />
            <StateBadge :label="opportunity.priorityLabel || 'Priority'" :tone="priorityTone(opportunity.priority)" />
            <StateBadge v-if="opportunity.favorited" label="Favorite" tone="amber" />
          </div>
          <h2 class="mt-3 text-xl font-semibold tracking-normal text-land-ink">{{ healthHeadline }}</h2>
          <div class="mt-3 flex flex-wrap gap-2">
            <StateBadge v-for="reason in health.reasons || []" :key="reason" :label="reason" :tone="health.tone || 'slate'" />
            <span v-if="(health.reasons || []).length === 0" class="text-sm text-land-ink-muted">No active blockers or overdue work.</span>
          </div>
        </div>
        <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
          <div class="flex items-center justify-between gap-3">
            <p class="text-sm font-semibold text-land-ink">Health score</p>
            <p class="text-2xl font-semibold tracking-normal">{{ health.score ?? 0 }}</p>
          </div>
          <ProgressBar class="mt-3" :value="health.score || 0" :tone="health.tone || 'slate'" />
          <p class="mt-2 text-xs text-land-ink-soft">{{ health.stageAgeLabel || 'New stage' }} in {{ opportunity.stageLabel }}</p>
        </div>
      </div>
      <div class="grid gap-px border-t border-land-border-soft bg-land-border-soft sm:grid-cols-2 xl:grid-cols-5">
        <article v-for="card in healthCards" :key="card.label" class="bg-land-surface px-4 py-3">
          <p class="text-xs font-semibold uppercase text-land-ink-soft">{{ card.label }}</p>
          <div class="mt-2 flex items-center justify-between gap-3">
            <p class="text-sm font-semibold text-land-ink">{{ card.value }}</p>
            <StateBadge :label="card.badge" :tone="card.tone" />
          </div>
        </article>
      </div>
    </section>

    <section class="mb-4 rounded-md border border-land-border bg-land-surface shadow-sm" data-testid="qualification-screening-panel">
      <div class="flex flex-col gap-3 border-b border-land-border-soft p-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p class="text-xs font-semibold uppercase text-land-ink-soft">Qualification phase 2.2</p>
          <div class="mt-1 flex flex-wrap items-center gap-2">
            <h2 class="text-lg font-semibold tracking-normal">Preliminary site screening</h2>
            <StateBadge :label="screeningSummary.statusLabel || 'Not run'" :tone="screeningSummary.statusTone || 'slate'" />
          </div>
          <p class="mt-1 text-sm text-land-ink-muted">{{ screeningSummaryText }}</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button type="button" class="inline-flex h-9 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" @click="openScreeningDrawer">
            Review
          </button>
          <button type="button" data-testid="run-qualification-screening" class="inline-flex h-9 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-wait disabled:opacity-60" :disabled="isRunningScreening" @click="runScreening()">
            <LoaderCircle v-if="isRunningScreening" class="size-4 animate-spin" aria-hidden="true" />
            <RefreshCw v-else class="size-4" aria-hidden="true" />
            Run screening
          </button>
        </div>
      </div>
      <div class="grid gap-px bg-land-border-soft md:grid-cols-4">
        <article v-for="item in screeningDecisionCards" :key="item.label" class="bg-land-surface px-4 py-3">
          <div class="flex items-center justify-between gap-3">
            <p class="text-xs font-semibold uppercase text-land-ink-soft">{{ item.label }}</p>
            <StateBadge :label="item.badge" :tone="item.tone" />
          </div>
          <p class="mt-2 text-lg font-semibold">{{ item.value }}</p>
          <p class="mt-1 text-xs leading-5 text-land-ink-muted">{{ item.detail }}</p>
          <p v-if="item.evidence" class="mt-2 text-xs font-medium text-land-ink">{{ item.evidence }}</p>
        </article>
      </div>
      <div class="grid gap-3 p-4 lg:grid-cols-2">
        <article v-for="finding in topScreeningFindings" :key="finding.key" class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-xs font-semibold uppercase text-land-ink-soft">{{ finding.categoryLabel }}</p>
              <p class="mt-1 truncate text-sm font-semibold">{{ finding.title }}</p>
              <p class="mt-1 text-xs leading-5 text-land-ink-soft">{{ screeningQuestion(finding) }}</p>
              <p class="mt-1 text-xs leading-5 text-land-ink-muted">{{ finding.summary }}</p>
            </div>
            <StateBadge :label="finding.statusLabel" :tone="finding.statusTone" />
          </div>
        </article>
      </div>
    </section>

    <section class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div class="space-y-4">
        <section class="rounded-md border border-land-border bg-land-surface shadow-sm" data-testid="opportunity-record-card">
          <div class="flex items-center justify-between gap-3 border-b border-land-border-soft p-4">
            <div>
              <h2 class="text-lg font-semibold tracking-normal">Opportunity record</h2>
              <p class="mt-1 text-sm text-land-ink-muted">{{ opportunity.name }}</p>
            </div>
            <div class="flex gap-2">
              <button v-if="!isEditing" type="button" data-testid="edit-opportunity-inline" class="inline-flex h-9 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover" @click="startEditing">
                <Pencil class="size-4" aria-hidden="true" />
                Edit opportunity
              </button>
              <button v-else type="button" class="inline-flex h-9 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-wait disabled:opacity-60" :disabled="isSaving" @click="cancelEditing">
                <X class="size-4" aria-hidden="true" />
                Cancel
              </button>
            </div>
          </div>

          <form v-if="isEditing" class="grid gap-4 p-4 sm:grid-cols-2" data-testid="opportunity-edit-form" @submit.prevent="saveOpportunity">
            <div v-if="formErrors.length > 0" class="sm:col-span-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              <p v-for="error in formErrors" :key="error">{{ error }}</p>
            </div>

            <label class="sm:col-span-2 text-sm font-medium">Opportunity name
              <input v-model.trim="editForm.name" data-testid="edit-opportunity-name" required type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="sm:col-span-2 text-sm font-medium">Internal project identifier
              <input v-model.trim="editForm.internal_identifier" data-testid="edit-opportunity-internal-identifier" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <div class="text-sm font-medium">Stage
              <SelectMenu v-model="editForm.stage" class="mt-2" :options="formOptions.stages" aria-label="Stage" test-id="edit-opportunity-stage" />
            </div>
            <div class="text-sm font-medium">Priority
              <SelectMenu v-model="editForm.priority" class="mt-2" :options="formOptions.priorities" aria-label="Priority" test-id="edit-opportunity-priority" />
            </div>
            <div class="text-sm font-medium">Assignee
              <SelectMenu v-model="editForm.assignee_id" class="mt-2" :options="assigneeOptions" aria-label="Assignee" test-id="edit-opportunity-assignee" />
            </div>
            <label class="text-sm font-medium">Source
              <input v-model.trim="editForm.source" data-testid="edit-opportunity-source" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">Market
              <input v-model.trim="editForm.market" data-testid="edit-opportunity-market" required type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">County
              <input v-model.trim="editForm.county" data-testid="edit-opportunity-county" required type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">State
              <input v-model.trim="editForm.state" data-testid="edit-opportunity-state" required maxlength="2" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm uppercase focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">Total acreage estimate
              <input v-model="editForm.acreage_estimate" data-testid="edit-opportunity-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">Target price
              <input v-model="editForm.target_price" data-testid="edit-opportunity-target-price" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">Asking price
              <input v-model="editForm.asking_price" data-testid="edit-opportunity-asking-price" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>
            <label class="text-sm font-medium">Next follow-up
              <input v-model="editForm.next_follow_up_at" data-testid="edit-opportunity-follow-up" type="datetime-local" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
            </label>

            <div class="sm:col-span-2 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
              <h3 class="text-sm font-semibold">Initial intake</h3>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <label class="text-sm font-medium">CRM / ClickUp fields
                  <input v-model.trim="editForm.crm_fields_status" data-testid="edit-opportunity-crm-fields-status" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Site control status
                  <input v-model.trim="editForm.site_control_status" data-testid="edit-opportunity-site-control-status" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Project sponsor
                  <input v-model.trim="editForm.project_sponsor" data-testid="edit-opportunity-project-sponsor" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Project originator
                  <input v-model.trim="editForm.project_originator" data-testid="edit-opportunity-project-originator" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Landowner(s)
                  <textarea v-model.trim="editForm.landowner_summary" data-testid="edit-opportunity-landowner-summary" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Asking price / economics
                  <textarea v-model.trim="editForm.economics_summary" data-testid="edit-opportunity-economics-summary" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Confidentiality requirements
                  <textarea v-model.trim="editForm.confidentiality_requirements" data-testid="edit-opportunity-confidentiality-requirements" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Available documents
                  <textarea v-model.trim="editForm.available_documents_summary" data-testid="edit-opportunity-available-documents" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="text-sm font-medium">Data room status
                  <input v-model.trim="editForm.data_room_status" data-testid="edit-opportunity-data-room-status" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Data room URL
                  <input v-model.trim="editForm.data_room_url" data-testid="edit-opportunity-data-room-url" type="url" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
              </div>
            </div>

            <div class="sm:col-span-2 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
              <h3 class="text-sm font-semibold">Internal project summary</h3>
              <div class="mt-3 grid gap-3 sm:grid-cols-3">
                <label class="sm:col-span-3 text-sm font-medium">Executive summary
                  <textarea v-model.trim="editForm.summary" data-testid="edit-opportunity-summary" rows="4" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Strategic rationale
                  <textarea v-model.trim="editForm.strategic_rationale" data-testid="edit-opportunity-strategic-rationale" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="text-sm font-medium">Immediate MW opportunity
                  <input v-model="editForm.immediate_mw_opportunity" data-testid="edit-opportunity-immediate-mw" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Intermediate MW opportunity
                  <input v-model="editForm.intermediate_mw_opportunity" data-testid="edit-opportunity-intermediate-mw" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Ultimate MW opportunity
                  <input v-model="editForm.ultimate_mw_opportunity" data-testid="edit-opportunity-ultimate-mw" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Utility territory
                  <input v-model.trim="editForm.utility_territory" data-testid="edit-opportunity-utility-territory" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Competitive positioning
                  <textarea v-model.trim="editForm.competitive_positioning" data-testid="edit-opportunity-competitive-positioning" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Risks / blockers identified
                  <textarea v-model.trim="editForm.risks_blockers_identified" data-testid="edit-opportunity-risks-blockers" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-3 text-sm font-medium">Recommended next steps
                  <textarea v-model.trim="editForm.recommended_next_steps" data-testid="edit-opportunity-recommended-next-steps" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
              </div>
            </div>

            <div class="sm:col-span-2 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
              <h3 class="text-sm font-semibold">Site information</h3>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <label class="text-sm font-medium">Ownership status
                  <input v-model.trim="editForm.ownership_status" data-testid="edit-opportunity-ownership-status" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Current zoning classification
                  <input v-model.trim="editForm.zoning_classification" data-testid="edit-opportunity-zoning-classification" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Estimated usable acreage
                  <input v-model="editForm.usable_acreage_estimate" data-testid="edit-opportunity-usable-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Estimated pad acreage
                  <input v-model="editForm.pad_acreage_estimate" data-testid="edit-opportunity-pad-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Expansion acreage
                  <input v-model="editForm.expansion_acreage_estimate" data-testid="edit-opportunity-expansion-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Site maps / GIS overlays
                  <textarea v-model.trim="editForm.gis_overlays_summary" data-testid="edit-opportunity-gis-overlays" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Existing structures / improvements
                  <textarea v-model.trim="editForm.existing_structures" data-testid="edit-opportunity-existing-structures" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <label class="sm:col-span-2 text-sm font-medium">Existing industrial use history
                  <textarea v-model.trim="editForm.industrial_use_history" data-testid="edit-opportunity-industrial-use-history" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
              </div>
            </div>

            <div class="sm:col-span-2 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
              <h3 class="text-sm font-semibold">Primary parcel</h3>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <label class="text-sm font-medium">Parcel/APN
                  <input v-model.trim="editForm.parcel_apn" data-testid="edit-parcel-apn" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Parcel acreage
                  <input v-model="editForm.parcel_acreage" data-testid="edit-parcel-acreage" min="0" step="0.01" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Address
                  <input v-model.trim="editForm.parcel_address_line1" data-testid="edit-parcel-address" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">City
                  <input v-model.trim="editForm.parcel_city" data-testid="edit-parcel-city" type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Latitude
                  <input v-model="editForm.parcel_latitude" data-testid="edit-parcel-latitude" step="0.000001" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <label class="text-sm font-medium">Longitude
                  <input v-model="editForm.parcel_longitude" data-testid="edit-parcel-longitude" step="0.000001" type="number" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
              </div>
            </div>

            <div class="sm:col-span-2 flex flex-col-reverse gap-2 border-t border-land-border-soft pt-4 sm:flex-row sm:justify-end">
              <button type="button" class="h-10 rounded-md border border-land-border bg-white px-4 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-wait disabled:opacity-60" :disabled="isSaving" @click="cancelEditing">Cancel</button>
              <button type="submit" data-testid="save-opportunity" class="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-land-primary px-4 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-wait disabled:opacity-60" :disabled="isSaving">
                <LoaderCircle v-if="isSaving" class="size-4 animate-spin" aria-hidden="true" />
                <Save v-else class="size-4" aria-hidden="true" />
                Save changes
              </button>
            </div>
          </form>

          <div v-else class="p-4">
            <div class="grid gap-4 md:grid-cols-4">
              <div class="text-sm font-medium">Quick stage
                <SelectMenu class="mt-2" :model-value="opportunity.stage" :options="formOptions.stages" aria-label="Quick stage" test-id="quick-stage-select" @update:model-value="updateStage" />
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Priority</p>
                <p class="mt-2 text-sm font-semibold">{{ opportunity.priorityLabel }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Assignee</p>
                <p class="mt-2 text-sm font-semibold">{{ opportunity.assignee }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Diligence</p>
                <p class="mt-2 text-sm font-semibold">{{ opportunity.diligenceProgress }}%</p>
              </div>
            </div>

            <div class="mt-4 grid gap-4 border-t border-land-border-soft pt-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Market</p>
                <p class="mt-2 text-sm font-semibold">{{ opportunity.market }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">County</p>
                <p class="mt-2 text-sm font-semibold">{{ opportunity.county }}, {{ opportunity.state }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Acreage</p>
                <p class="mt-2 text-sm font-semibold">{{ formatNumber(opportunity.acreage) }} acres</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Next follow-up</p>
                <p class="mt-2 text-sm font-semibold">{{ formatDateTime(opportunity.nextFollowUpAt) }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Source</p>
                <p class="mt-2 text-sm font-semibold">{{ opportunity.source || 'Not set' }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Target price</p>
                <p class="mt-2 text-sm font-semibold">{{ formatCurrency(opportunity.targetPriceCents) }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Asking price</p>
                <p class="mt-2 text-sm font-semibold">{{ formatCurrency(opportunity.askingPriceCents) }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-land-ink-muted">Primary parcel</p>
                <p class="mt-2 text-sm font-semibold">{{ primaryParcel?.displayName || 'Not linked' }}</p>
              </div>
            </div>

            <div class="mt-4 border-t border-land-border-soft pt-4">
              <h3 class="text-sm font-semibold text-land-ink">Initial intake</h3>
              <div class="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">CRM / ClickUp fields</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.crmFieldsStatus || 'Not set' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Site control</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.siteControlStatus || 'Not set' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Project sponsor</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.projectSponsor || 'Not set' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Project originator</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.projectOriginator || 'Not set' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Data room status</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.dataRoomStatus || 'Not set' }}</p>
                </div>
                <div class="lg:col-span-3">
                  <p class="text-sm font-medium text-land-ink-muted">Data room URL</p>
                  <a v-if="opportunity.dataRoomUrl" :href="opportunity.dataRoomUrl" target="_blank" rel="noreferrer" class="mt-2 inline-flex max-w-full truncate text-sm font-semibold text-land-primary hover:text-land-primary-hover">{{ opportunity.dataRoomUrl }}</a>
                  <p v-else class="mt-2 text-sm font-semibold">Not set</p>
                </div>
              </div>
              <div class="mt-4 grid gap-3 lg:grid-cols-2">
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Landowner(s)</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.landownerSummary || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Asking price / economics</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.economicsSummary || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Confidentiality requirements</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.confidentialityRequirements || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Available documents</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.availableDocumentsSummary || 'Not documented' }}</p>
                </div>
              </div>
            </div>

            <div class="mt-4 border-t border-land-border-soft pt-4">
              <h3 class="text-sm font-semibold text-land-ink">Internal project summary</h3>
              <div class="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Immediate MW</p>
                  <p class="mt-2 text-sm font-semibold">{{ formatMw(opportunity.immediateMwOpportunity) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Intermediate MW</p>
                  <p class="mt-2 text-sm font-semibold">{{ formatMw(opportunity.intermediateMwOpportunity) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Ultimate MW</p>
                  <p class="mt-2 text-sm font-semibold">{{ formatMw(opportunity.ultimateMwOpportunity) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Utility territory</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.utilityTerritory || 'Not set' }}</p>
                </div>
              </div>
              <div class="mt-4 grid gap-3 lg:grid-cols-2">
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3 lg:col-span-2">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Executive summary</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.executiveSummary || opportunity.summary || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Strategic rationale</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.strategicRationale || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Competitive positioning</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.competitivePositioning || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Risks / blockers identified</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.risksBlockersIdentified || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Recommended next steps</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.recommendedNextSteps || 'Not documented' }}</p>
                </div>
              </div>
            </div>

            <div class="mt-4 border-t border-land-border-soft pt-4">
              <h3 class="text-sm font-semibold text-land-ink">Site information</h3>
              <div class="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Project name</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.name }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Internal ID</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.internalIdentifier || 'Not set' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Ownership status</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.ownershipStatus || 'Not set' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Current zoning</p>
                  <p class="mt-2 text-sm font-semibold">{{ opportunity.zoningClassification || 'Not set' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Total acreage</p>
                  <p class="mt-2 text-sm font-semibold">{{ formatAcres(opportunity.acreage || opportunity.acreageEstimate) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Usable acreage</p>
                  <p class="mt-2 text-sm font-semibold">{{ formatAcres(opportunity.usableAcreageEstimate) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Pad acreage</p>
                  <p class="mt-2 text-sm font-semibold">{{ formatAcres(opportunity.padAcreageEstimate) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Expansion acreage</p>
                  <p class="mt-2 text-sm font-semibold">{{ formatAcres(opportunity.expansionAcreageEstimate) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">Parcel IDs</p>
                  <p class="mt-2 text-sm font-semibold">{{ parcelIdentifiers }}</p>
                </div>
                <div class="lg:col-span-2">
                  <p class="text-sm font-medium text-land-ink-muted">Street address</p>
                  <p class="mt-2 text-sm font-semibold">{{ siteAddress }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-land-ink-muted">GPS coordinates</p>
                  <p class="mt-2 text-sm font-semibold">{{ siteCoordinates }}</p>
                </div>
              </div>
              <div class="mt-4 grid gap-3 lg:grid-cols-3">
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Site maps / GIS overlays</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.gisOverlaysSummary || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Structures / improvements</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.existingStructures || 'Not documented' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Industrial use history</p>
                  <p class="mt-2 whitespace-pre-line text-sm leading-6 text-land-ink-muted">{{ opportunity.industrialUseHistory || 'Not documented' }}</p>
                </div>
              </div>
            </div>

          </div>
        </section>

        <ImageDropzone
          title="Opportunity images"
          :upload-url="`/opportunities/${opportunity.id}/images`"
          :images="images"
          @updated="updateOpportunityImages"
        />

        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold tracking-normal">Diligence</h2>
                <p class="mt-1 text-sm text-land-ink-muted">
                  {{ formatNumber(opportunity.diligenceSummary?.completed) }}/{{ formatNumber(opportunity.diligenceSummary?.total) }} complete
                  <span v-if="opportunity.diligenceSummary?.blocked"> · {{ formatNumber(opportunity.diligenceSummary.blocked) }} blocked</span>
                  <span v-if="opportunity.diligenceSummary?.overdue"> · {{ formatNumber(opportunity.diligenceSummary.overdue) }} overdue</span>
                </p>
              </div>
              <div class="flex flex-wrap gap-2" aria-label="Diligence filters">
                <button
                  v-for="filter in diligenceFilters"
                  :key="filter.value"
                  type="button"
                  class="h-8 rounded-md border px-2.5 text-xs font-semibold"
                  :class="selectedDiligenceFilter === filter.value ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
                  @click="selectedDiligenceFilter = filter.value"
                >
                  {{ filter.label }}
                </button>
              </div>
            </div>
          </div>
          <div class="divide-y divide-land-border-soft">
            <article v-for="item in filteredDiligence" :key="item.id" class="border-l-4 px-4 py-3" :class="itemToneBorder(item)">
              <form v-if="editingDiligenceId === item.id" class="grid gap-3 md:grid-cols-2" :data-testid="`diligence-edit-form-${item.id}`" @submit.prevent="saveDiligenceItem(item)">
                <div v-if="diligenceErrors.length > 0" class="md:col-span-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                  <p v-for="error in diligenceErrors" :key="error">{{ error }}</p>
                </div>
                <label class="md:col-span-2 text-sm font-medium">Title
                  <input v-model.trim="diligenceForm.title" data-testid="diligence-title" required type="text" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <div class="text-sm font-medium">Status
                  <SelectMenu v-model="diligenceForm.status" class="mt-2" :options="formOptions.diligenceStatuses" aria-label="Diligence status" test-id="diligence-status" />
                </div>
                <label class="text-sm font-medium">Due date
                  <input v-model="diligenceForm.due_on" data-testid="diligence-due-on" type="date" class="mt-2 h-10 w-full rounded-md border border-land-border px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                </label>
                <div class="text-sm font-medium">Assignee
                  <SelectMenu v-model="diligenceForm.assignee_id" class="mt-2" :options="assigneeOptions" aria-label="Diligence assignee" test-id="diligence-assignee" />
                </div>
                <label class="flex items-center gap-2 self-end pb-2 text-sm font-medium">
                  <input v-model="diligenceForm.blocking" data-testid="diligence-blocking" type="checkbox" class="size-4 rounded border-land-border text-land-primary focus:ring-land-primary/20">
                  Blocking item
                </label>
                <label class="md:col-span-2 text-sm font-medium">Description
                  <textarea v-model.trim="diligenceForm.description" data-testid="diligence-description" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" />
                </label>
                <div class="md:col-span-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button type="button" class="h-9 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-wait disabled:opacity-60" :disabled="savingDiligenceId === item.id" @click="cancelDiligenceEdit">Cancel</button>
                  <button type="submit" data-testid="save-diligence-item" class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-wait disabled:opacity-60" :disabled="savingDiligenceId === item.id">
                    <LoaderCircle v-if="savingDiligenceId === item.id" class="size-4 animate-spin" aria-hidden="true" />
                    <Save v-else class="size-4" aria-hidden="true" />
                    Save diligence
                  </button>
                </div>
              </form>
              <div v-else class="grid gap-3 md:grid-cols-[minmax(0,1fr)_150px_130px_88px] md:items-start">
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium">{{ item.title }}</p>
                  <p class="mt-1 text-xs text-land-ink-soft">{{ item.category }} · {{ item.assignee }}</p>
                  <div class="mt-2 flex flex-wrap gap-1.5">
                    <StateBadge :label="item.sourceLabel" :tone="item.source === 'clickup' ? 'blue' : 'slate'" />
                    <StateBadge v-if="item.screeningFinding" :label="`Screening ${item.screeningFinding.statusLabel}`" :tone="item.screeningFinding.statusTone || 'slate'" />
                    <StateBadge v-if="item.blocking" label="Blocking" tone="red" />
                  </div>
                  <p v-if="item.description" class="mt-2 line-clamp-2 text-xs leading-5 text-land-ink-muted">{{ item.description }}</p>
                </div>
                <StateBadge :label="item.statusLabel" :tone="item.statusTone || 'slate'" />
                <StateBadge :label="item.dueLabel || item.dueOn || 'No due date'" :tone="item.dueTone || 'slate'" />
                <button type="button" class="inline-flex h-8 items-center justify-center gap-1 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted" :aria-label="`Edit ${item.title}`" @click="startDiligenceEdit(item)">
                  <Pencil class="size-3.5" aria-hidden="true" />
                  Edit
                </button>
              </div>
            </article>
            <div v-if="filteredDiligence.length === 0" class="px-4 py-8 text-center text-sm text-land-ink-muted">No diligence items in this view.</div>
          </div>
        </section>
      </div>

      <aside class="space-y-4">
        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <h2 class="text-lg font-semibold tracking-normal">ClickUp</h2>
          </div>
          <div class="space-y-3 p-4 text-sm">
            <div class="flex items-center justify-between gap-3">
              <span class="text-land-ink-muted">Status</span>
              <span class="rounded-md bg-land-surface-muted px-2 py-1 text-xs font-semibold text-land-ink-muted">{{ clickup.statusLabel || 'Not synced' }}</span>
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-land-ink-muted">List</span>
              <span class="text-right font-medium">{{ clickup.listName || 'Not linked' }}</span>
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-land-ink-muted">Last sync</span>
              <span class="text-right font-medium">{{ formatDateTime(clickup.lastSyncedAt) }}</span>
            </div>
            <p v-if="clickup.error" class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{{ clickup.error }}</p>
            <div class="flex gap-2">
              <button type="button" class="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-not-allowed disabled:opacity-60" :disabled="isSyncing || !workspace.clickupEnabled" @click="syncOpportunity">
                <LoaderCircle v-if="isSyncing" class="size-4 animate-spin" aria-hidden="true" />
                <UploadCloud v-else class="size-4" aria-hidden="true" />
                Push
              </button>
              <a v-if="clickup.linked" :href="clickup.url" target="_blank" rel="noreferrer" class="inline-flex h-9 items-center justify-center rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" aria-label="Open ClickUp task">
                <ExternalLink class="size-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <h2 class="text-lg font-semibold tracking-normal">Parcels</h2>
          </div>
          <div class="divide-y divide-land-border-soft">
            <article v-for="parcel in parcels" :key="parcel.id" class="px-4 py-3">
              <div class="flex items-start justify-between gap-3">
                <p class="text-sm font-medium">{{ parcel.displayName }}</p>
                <StateBadge v-if="parcel.source" label="Public source" tone="amber" />
              </div>
              <p class="mt-1 text-xs text-land-ink-soft">{{ parcel.address || `${parcel.county}, ${parcel.state}` }}</p>
              <p v-if="parcel.source?.ownerName" class="mt-1 text-xs text-land-ink-soft">{{ parcel.source.ownerName }}</p>
              <p v-if="parcel.latitude && parcel.longitude" class="mt-2 text-xs text-land-ink-soft">{{ parcel.latitude }}, {{ parcel.longitude }}</p>
              <div class="mt-3">
                <ImageDropzone
                  title="Parcel images"
                  :upload-url="`/parcels/${parcel.id}/images`"
                  :images="parcel.images || []"
                  :framed="false"
                  @updated="(updatedImages) => updateParcelImages(parcel.id, updatedImages)"
                />
              </div>
            </article>
            <div v-if="parcels.length === 0" class="px-4 py-8 text-center text-sm text-land-ink-muted">No parcels linked yet.</div>
          </div>
        </section>

        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h2 class="text-lg font-semibold tracking-normal">Ownership Contacts</h2>
                <p class="mt-1 text-sm text-land-ink-muted">Owner-of-record contacts synced from tracked parcel sources.</p>
              </div>
              <StateBadge :label="`${contacts.length}`" :tone="contacts.length > 0 ? 'green' : 'slate'" />
            </div>
          </div>
          <div class="divide-y divide-land-border-soft">
            <article v-for="contact in contacts" :key="contact.id" class="px-4 py-3">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold">{{ contact.name }}</p>
                  <p v-if="contact.companyName" class="mt-1 text-xs text-land-ink-soft">{{ contact.companyName }}</p>
                  <p v-if="contact.mailingAddress" class="mt-1 text-xs text-land-ink-soft">{{ contact.mailingAddress }}</p>
                  <p v-if="contact.parcelOwnerApn" class="mt-1 text-xs text-land-ink-soft">APN {{ contact.parcelOwnerApn }} · {{ contact.parcelOwnerSource }}</p>
                </div>
                <div class="flex shrink-0 flex-col items-end gap-1.5">
                  <StateBadge :label="contact.roleLabel || 'Owner'" tone="amber" />
                  <StateBadge v-if="contact.primary" label="Primary" tone="green" />
                </div>
              </div>
            </article>
            <div v-if="contacts.length === 0" class="px-4 py-8 text-center text-sm text-land-ink-muted">No ownership contacts synced yet.</div>
          </div>
        </section>

        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h2 class="text-lg font-semibold tracking-normal">Tasks</h2>
                <p class="mt-1 text-sm text-land-ink-muted">
                  {{ formatNumber(opportunity.taskSummary?.open) }} open
                  <span v-if="opportunity.taskSummary?.overdue"> · {{ formatNumber(opportunity.taskSummary.overdue) }} overdue</span>
                </p>
              </div>
              <StateBadge :label="`${formatNumber(opportunity.taskSummary?.completed)} done`" tone="green" />
            </div>
            <div class="mt-3 flex flex-wrap gap-2" aria-label="Task filters">
              <button
                v-for="filter in taskFilters"
                :key="filter.value"
                type="button"
                class="h-8 rounded-md border px-2.5 text-xs font-semibold"
                :class="selectedTaskFilter === filter.value ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
                @click="selectedTaskFilter = filter.value"
              >
                {{ filter.label }}
              </button>
            </div>
          </div>
          <div class="divide-y divide-land-border-soft">
            <article v-for="task in filteredTasks" :key="task.id" class="border-l-4 px-4 py-3" :class="itemToneBorder(task)">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium">{{ task.title }}</p>
                  <p class="mt-1 text-xs text-land-ink-soft">{{ task.assignee }} · {{ task.sourceLabel }}</p>
                  <div class="mt-2 flex flex-wrap gap-1.5">
                    <StateBadge :label="task.dueLabel" :tone="task.dueTone || 'slate'" />
                    <StateBadge :label="task.priorityLabel" :tone="task.priorityTone || 'slate'" />
                    <StateBadge :label="task.statusLabel" :tone="task.statusTone || 'slate'" />
                  </div>
                </div>
                <button v-if="task.status !== 'completed'" type="button" class="h-8 rounded-md bg-land-primary px-2 text-xs font-semibold text-white hover:bg-land-primary-hover" @click="completeTask(task)">Complete</button>
              </div>
            </article>
            <div v-if="filteredTasks.length === 0" class="px-4 py-8 text-center text-sm text-land-ink-muted">No tasks in this view.</div>
          </div>
        </section>
      </aside>
    </section>

    <div v-if="showScreeningDrawer" class="fixed inset-0 z-50 bg-slate-950/40" role="dialog" aria-modal="true" aria-labelledby="screening-drawer-title" @keydown.escape="closeScreeningDrawer">
      <button type="button" class="absolute inset-0 size-full cursor-default" aria-label="Close screening drawer" @click="closeScreeningDrawer" />
      <aside class="relative ml-auto flex h-full w-full max-w-5xl flex-col overflow-hidden border-l border-land-border bg-white shadow-xl">
        <div class="flex items-start justify-between gap-4 border-b border-land-border-soft p-5">
          <div>
            <p class="text-xs font-semibold uppercase text-land-ink-soft">Qualification screening</p>
            <h2 id="screening-drawer-title" class="mt-1 text-xl font-semibold tracking-normal">Preliminary site screening review</h2>
            <p class="mt-1 text-sm text-land-ink-muted">{{ screeningSummaryText }} · {{ screeningSummary.nextAction || 'Review generated findings before advancing.' }}</p>
          </div>
          <button type="button" class="grid size-9 place-items-center rounded-md text-land-ink-muted hover:bg-land-surface-muted" aria-label="Close screening drawer" @click="closeScreeningDrawer">
            <X class="size-5" aria-hidden="true" />
          </button>
        </div>

        <div class="border-b border-land-border-soft p-4">
          <div class="mb-4 grid gap-3 md:grid-cols-4">
            <article v-for="item in screeningDecisionCards" :key="`drawer-${item.label}`" class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
              <div class="flex items-center justify-between gap-3">
                <p class="text-xs font-semibold uppercase text-land-ink-soft">{{ item.label }}</p>
                <StateBadge :label="item.badge" :tone="item.tone" />
              </div>
              <p class="mt-2 text-lg font-semibold">{{ item.value }}</p>
              <p class="mt-1 text-xs leading-5 text-land-ink-muted">{{ item.detail }}</p>
            </article>
          </div>
          <div class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <label class="block text-sm font-medium">Radius
              <div class="mt-2 flex items-center gap-3">
                <input v-model.number="screeningRadius" min="1" max="50" step="1" type="range" class="w-48 accent-land-primary" aria-label="Screening radius">
                <span class="w-14 text-sm font-semibold text-land-ink-muted">{{ screeningRadius }} mi</span>
              </div>
            </label>
            <div class="flex flex-wrap gap-2">
              <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-wait disabled:opacity-60" :disabled="isRunningScreening || selectedScreeningCategory === 'overview'" @click="runScreening(activeScreeningKeys)">
                <LoaderCircle v-if="isRunningScreening" class="size-4 animate-spin" aria-hidden="true" />
                <RefreshCw v-else class="size-4" aria-hidden="true" />
                Run tab
              </button>
              <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-wait disabled:opacity-60" :disabled="isRunningScreening" @click="runScreening()">
                <LoaderCircle v-if="isRunningScreening" class="size-4 animate-spin" aria-hidden="true" />
                <RefreshCw v-else class="size-4" aria-hidden="true" />
                Run all
              </button>
            </div>
          </div>
          <div class="mt-4 flex gap-2 overflow-x-auto" aria-label="Screening categories">
            <button
              v-for="category in screeningCategoryTabs"
              :key="category.key"
              type="button"
              class="h-9 shrink-0 rounded-md border px-3 text-sm font-semibold"
              :class="selectedScreeningCategory === category.key ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
              @click="selectedScreeningCategory = category.key"
            >
              {{ category.label }}
              <span class="ml-1 text-xs opacity-70">{{ category.count }}</span>
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-4">
          <div v-if="screeningErrors.length > 0" class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            <p v-for="error in screeningErrors" :key="error">{{ error }}</p>
          </div>

          <div class="grid gap-3">
            <article v-for="finding in activeScreeningFindings" :key="finding.key" class="rounded-md border border-land-border bg-land-surface p-4 shadow-sm" :data-testid="`screening-finding-${finding.key}`">
              <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <StateBadge :label="finding.statusLabel" :tone="finding.statusTone" />
                    <StateBadge :label="finding.confidenceLabel" :tone="finding.confidenceTone" />
                    <StateBadge v-if="finding.diligenceStatusLabel" :label="`Diligence ${finding.diligenceStatusLabel}`" tone="slate" />
                    <StateBadge :label="`${formatNumber(finding.sourceCount || 0)} sources`" :tone="sourceCoverageTone(finding)" />
                  </div>
                  <h3 class="mt-3 text-base font-semibold tracking-normal">{{ finding.title }}</h3>
                  <p class="mt-2 text-sm font-medium text-land-ink">{{ screeningQuestion(finding) }}</p>
                  <p class="mt-2 text-sm leading-6 text-land-ink-muted">{{ finding.summary }}</p>
                  <p v-if="finding.recommendation" class="mt-2 text-sm leading-6 text-land-ink">{{ finding.recommendation }}</p>
                </div>
                <div class="shrink-0 rounded-md border border-land-border-soft bg-land-surface-muted p-3 lg:w-64">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Diligence action</p>
                  <button type="button" class="mt-2 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface disabled:cursor-not-allowed disabled:opacity-60" :disabled="!canApplyScreeningFinding(finding) || isApplyingFinding(finding.id)" @click="applyScreeningFinding(finding)">
                    <LoaderCircle v-if="isApplyingFinding(finding.id)" class="size-4 animate-spin" aria-hidden="true" />
                    <Save v-else class="size-4" aria-hidden="true" />
                    {{ screeningActionLabel(finding) }}
                  </button>
                  <p class="mt-2 text-xs leading-5 text-land-ink-muted">{{ screeningActionDetail(finding) }}</p>
                </div>
              </div>

              <div class="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px]">
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Decision signal</p>
                  <p class="mt-2 text-sm font-semibold text-land-ink">{{ screeningDecisionSignal(finding) }}</p>
                  <dl class="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <dt class="text-xs font-semibold uppercase text-land-ink-soft">Score</dt>
                      <dd class="mt-1 font-semibold">{{ finding.score === null || finding.score === undefined ? 'Needs review' : `${finding.score}/100` }}</dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold uppercase text-land-ink-soft">Confidence</dt>
                      <dd class="mt-1 font-semibold">{{ formatNumber(finding.confidenceScore || 0) }}/100</dd>
                    </div>
                  </dl>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Metrics</p>
                  <dl class="mt-2 space-y-2 text-sm">
                    <div v-for="metric in metricEntries(finding.metrics)" :key="metric.key" class="flex justify-between gap-3">
                      <dt class="text-land-ink-muted">{{ metric.label }}</dt>
                      <dd class="text-right font-semibold">{{ metric.value }}</dd>
                    </div>
                    <p v-if="metricEntries(finding.metrics).length === 0" class="text-xs text-land-ink-muted">No structured metrics yet.</p>
                  </dl>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Sources</p>
                  <div class="mt-2 space-y-2">
                    <div v-for="source in finding.sources" :key="`${finding.key}-${source.name}-${source.status}`" class="text-sm">
                      <a v-if="source.url" :href="source.url" target="_blank" rel="noreferrer" class="font-semibold text-land-primary hover:text-land-primary-hover">{{ source.name }}</a>
                      <p v-else class="font-semibold">{{ source.name }}</p>
                      <p class="text-xs leading-5 text-land-ink-muted">{{ source.provider || 'Source' }} · {{ titleize(source.status || 'planned') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </aside>
    </div>
  </AppShell>
</template>

<script setup>
import { ArrowLeft, ExternalLink, LoaderCircle, MapPinned, Pencil, RefreshCw, Save, Star, UploadCloud, X } from '@lucide/vue'
import { computed, ref } from 'vue'
import { normalizeErrors, requestJson } from '@/api/client'
import ImageDropzone from '@/components/shared/image_dropzone.vue'
import AppShell from '@/components/shared/app_shell.vue'
import ProgressBar from '@/components/shared/progress_bar.vue'
import SelectMenu from '@/components/shared/select_menu.vue'
import StateBadge from '@/components/shared/state_badge.vue'

const props = defineProps({
  payload: {
    type: Object,
    required: true
  }
})

const detail = ref(props.payload)
const notice = ref('')
const errors = ref([])
const formErrors = ref([])
const editForm = ref({})
const diligenceForm = ref({})
const diligenceErrors = ref([])
const isEditing = ref(false)
const isSaving = ref(false)
const isSyncing = ref(false)
const isFavoriting = ref(false)
const isRunningScreening = ref(false)
const editingDiligenceId = ref(null)
const savingDiligenceId = ref(null)
const showScreeningDrawer = ref(false)
const selectedScreeningCategory = ref('overview')
const screeningRadius = ref(10)
const screeningErrors = ref([])
const applyingFindingIds = ref([])
const selectedDiligenceFilter = ref('all')
const selectedTaskFilter = ref('open')

const workspace = computed(() => detail.value.workspace || {})
const formOptions = computed(() => detail.value.formOptions || { stages: [], priorities: [], diligenceStatuses: [], users: [] })
const assigneeOptions = computed(() => [
  { value: '', label: 'Unassigned' },
  ...(formOptions.value.users || [])
])
const opportunity = computed(() => detail.value.opportunity || {})
const mapOpportunityPath = computed(() => `/map?opportunity_id=${encodeURIComponent(opportunity.value.id || '')}`)
const health = computed(() => opportunity.value.health || {})
const healthHeadline = computed(() => ({
  blocked: 'Resolve blockers before advancing',
  overdue: 'Resolve overdue acquisition work',
  attention: 'Review items needing attention',
  stale: 'Refresh ClickUp before acting',
  incomplete: 'Complete core opportunity data',
  healthy: 'Opportunity is ready for continued review',
  closed: 'Opportunity is closed'
})[health.value.state] || health.value.nextAction || 'Review opportunity state')
const clickup = computed(() => opportunity.value.clickup || { linked: false, statusLabel: 'Not synced' })
const images = computed(() => detail.value.images || [])
const parcels = computed(() => detail.value.parcels || [])
const contacts = computed(() => detail.value.contacts || [])
const primaryParcel = computed(() => parcels.value.find((parcel) => parcel.primary) || parcels.value[0] || null)
const parcelIdentifiers = computed(() => parcels.value.map((parcel) => parcel.apn).filter(Boolean).join(', ') || 'Not linked')
const siteAddress = computed(() => primaryParcel.value?.address || primaryParcel.value?.addressLine1 || 'Not set')
const siteCoordinates = computed(() => {
  if (primaryParcel.value?.latitude === null || primaryParcel.value?.latitude === undefined || primaryParcel.value?.longitude === null || primaryParcel.value?.longitude === undefined) return 'Not set'

  return `${primaryParcel.value.latitude}, ${primaryParcel.value.longitude}`
})
const diligence = computed(() => detail.value.diligence || [])
const tasks = computed(() => detail.value.tasks || [])
const screening = computed(() => detail.value.screening || { categories: [], summary: {}, findings: [] })
const screeningFindings = computed(() => screening.value.findings || [])
const screeningSummary = computed(() => screening.value.summary || {})
const screeningSummaryText = computed(() => {
  if (!screeningSummary.value.lastRunAt) return 'Screening has not been run for this opportunity.'

  return `${formatNumber(screeningSummary.value.run)} of ${formatNumber(screeningSummary.value.total)} checks run · ${formatDateTime(screeningSummary.value.lastRunAt)}`
})
const screeningDecisionCards = computed(() => {
  const run = Number(screeningSummary.value.run || 0)
  const total = Number(screeningSummary.value.total || 0)
  const reviewRequired = Number(screeningSummary.value.reviewRequired || 0)
  const blocked = Number(screeningSummary.value.blocked || 0)
  const sourceGaps = Number(screeningSummary.value.unavailable || 0) + Number(screeningSummary.value.failed || 0)
  const availableSources = screeningFindings.value.reduce((sum, finding) => sum + sourceStatusCount(finding, 'available'), 0)
  const plannedSources = screeningFindings.value.reduce((sum, finding) => sum + sourceStatusCount(finding, 'planned') + sourceStatusCount(finding, 'manual_required') + sourceStatusCount(finding, 'recommended'), 0)
  const topReview = screeningFindings.value.find((finding) => finding.status === 'review_required')
  const topBlocked = screeningFindings.value.find((finding) => ['blocked', 'failed', 'unavailable'].includes(finding.status))

  return [
    {
      label: 'Coverage',
      value: total > 0 ? `${formatNumber(run)}/${formatNumber(total)}` : 'Not run',
      detail: total > 0 && run === total ? 'Every qualification question has a current finding.' : 'Some qualification questions still need a saved finding.',
      evidence: `${formatNumber(availableSources)} live sources attached`,
      badge: run === total && total > 0 ? 'Complete' : 'Partial',
      tone: run === total && total > 0 ? 'green' : 'amber'
    },
    {
      label: 'Judgment',
      value: formatNumber(reviewRequired),
      detail: reviewRequired > 0 ? 'Findings need human interpretation before stage movement.' : 'No findings are currently marked for human review.',
      evidence: topReview?.title,
      badge: reviewRequired > 0 ? 'Review' : 'Clear',
      tone: reviewRequired > 0 ? 'amber' : 'green'
    },
    {
      label: 'Blockers',
      value: formatNumber(blocked),
      detail: blocked > 0 ? 'These findings should stop qualification progress until resolved.' : 'No screening findings are blocking progress.',
      evidence: topBlocked?.title,
      badge: blocked > 0 ? 'Blocked' : 'None',
      tone: blocked > 0 ? 'red' : 'green'
    },
    {
      label: 'Evidence gaps',
      value: formatNumber(sourceGaps),
      detail: sourceGaps > 0 ? 'One or more configured data sources failed or returned no usable data.' : 'Configured screening sources returned usable results.',
      evidence: plannedSources > 0 ? `${formatNumber(plannedSources)} recommended/manual sources remain` : null,
      badge: sourceGaps > 0 ? 'Gaps' : 'Ready',
      tone: sourceGaps > 0 ? 'red' : 'blue'
    }
  ]
})
const topScreeningFindings = computed(() => {
  const ranked = [...screeningFindings.value].sort((left, right) => screeningFindingRank(left) - screeningFindingRank(right))

  return ranked.slice(0, 4)
})
const screeningCategoryTabs = computed(() => [
  { key: 'overview', label: 'Overview', count: screeningFindings.value.length },
  ...(screening.value.categories || []).map((category) => ({
    key: category.key,
    label: category.label,
    count: screeningFindings.value.filter((finding) => finding.category === category.key).length
  }))
])
const activeScreeningFindings = computed(() => {
  if (selectedScreeningCategory.value === 'overview') return screeningFindings.value

  return screeningFindings.value.filter((finding) => finding.category === selectedScreeningCategory.value)
})
const activeScreeningKeys = computed(() => activeScreeningFindings.value.map((finding) => finding.key))
const healthCards = computed(() => [
  {
    label: 'Diligence',
    value: `${formatNumber(opportunity.value.diligenceSummary?.completed)}/${formatNumber(opportunity.value.diligenceSummary?.total)} complete`,
    badge: `${formatNumber(opportunity.value.diligenceProgress)}%`,
    tone: progressTone(opportunity.value)
  },
  {
    label: 'Work',
    value: `${formatNumber(opportunity.value.taskSummary?.open)} open tasks`,
    badge: opportunity.value.taskSummary?.overdue ? `${formatNumber(opportunity.value.taskSummary.overdue)} overdue` : `${formatNumber(opportunity.value.taskSummary?.dueSoon)} due soon`,
    tone: opportunity.value.taskSummary?.overdue ? 'red' : opportunity.value.taskSummary?.dueSoon ? 'amber' : 'slate'
  },
  {
    label: 'Map',
    value: `${formatNumber(opportunity.value.parcelSummary?.mappedCount)}/${formatNumber(opportunity.value.parcelSummary?.total)} parcels mapped`,
    badge: opportunity.value.parcelSummary?.mapped ? 'Mapped' : 'Needs coords',
    tone: opportunity.value.parcelSummary?.mapped ? 'green' : 'amber'
  },
  {
    label: 'Images',
    value: `${formatNumber(opportunity.value.imageSummary?.count)} opportunity images`,
    badge: opportunity.value.imageSummary?.count ? 'Present' : 'Missing',
    tone: opportunity.value.imageSummary?.count ? 'green' : 'slate'
  },
  {
    label: 'ClickUp',
    value: clickup.value.listName || 'Not linked',
    badge: clickup.value.healthLabel || clickup.value.statusLabel || 'Not synced',
    tone: clickup.value.healthTone || 'slate'
  }
])
const diligenceFilters = computed(() => [
  { value: 'all', label: `All ${formatNumber(diligence.value.length)}` },
  { value: 'open', label: `Open ${formatNumber(diligence.value.filter((item) => !item.completed).length)}` },
  { value: 'blocked', label: `Blocked ${formatNumber(diligence.value.filter((item) => item.blocking || item.status === 'blocked').length)}` },
  { value: 'overdue', label: `Overdue ${formatNumber(diligence.value.filter((item) => item.overdue).length)}` },
  { value: 'complete', label: `Complete ${formatNumber(diligence.value.filter((item) => item.completed).length)}` }
])
const taskFilters = computed(() => [
  { value: 'open', label: `Open ${formatNumber(tasks.value.filter((task) => task.status !== 'completed' && task.status !== 'canceled').length)}` },
  { value: 'overdue', label: `Overdue ${formatNumber(tasks.value.filter((task) => task.overdue).length)}` },
  { value: 'due_soon', label: `Due soon ${formatNumber(tasks.value.filter((task) => task.dueSoon).length)}` },
  { value: 'complete', label: `Complete ${formatNumber(tasks.value.filter((task) => task.status === 'completed').length)}` },
  { value: 'all', label: `All ${formatNumber(tasks.value.length)}` }
])
const filteredDiligence = computed(() => diligence.value.filter((item) => ({
  all: true,
  open: !item.completed,
  blocked: item.blocking || item.status === 'blocked',
  overdue: item.overdue,
  complete: item.completed
})[selectedDiligenceFilter.value] ?? true))
const filteredTasks = computed(() => tasks.value.filter((task) => ({
  all: true,
  open: task.status !== 'completed' && task.status !== 'canceled',
  overdue: task.overdue,
  due_soon: task.dueSoon,
  complete: task.status === 'completed'
})[selectedTaskFilter.value] ?? true))

const defaultEditForm = () => ({
  name: opportunity.value.name || '',
  stage: opportunity.value.stage || 'lead',
  priority: opportunity.value.priority || 'normal',
  assignee_id: opportunity.value.assigneeId || '',
  internal_identifier: opportunity.value.internalIdentifier || '',
  source: opportunity.value.source || '',
  market: opportunity.value.market || '',
  county: opportunity.value.county || '',
  state: opportunity.value.state || '',
  acreage_estimate: valueForInput(opportunity.value.acreageEstimate),
  crm_fields_status: opportunity.value.crmFieldsStatus || '',
  project_sponsor: opportunity.value.projectSponsor || '',
  project_originator: opportunity.value.projectOriginator || '',
  landowner_summary: opportunity.value.landownerSummary || '',
  site_control_status: opportunity.value.siteControlStatus || '',
  economics_summary: opportunity.value.economicsSummary || '',
  confidentiality_requirements: opportunity.value.confidentialityRequirements || '',
  available_documents_summary: opportunity.value.availableDocumentsSummary || '',
  data_room_url: opportunity.value.dataRoomUrl || '',
  data_room_status: opportunity.value.dataRoomStatus || '',
  strategic_rationale: opportunity.value.strategicRationale || '',
  immediate_mw_opportunity: valueForInput(opportunity.value.immediateMwOpportunity),
  intermediate_mw_opportunity: valueForInput(opportunity.value.intermediateMwOpportunity),
  ultimate_mw_opportunity: valueForInput(opportunity.value.ultimateMwOpportunity),
  utility_territory: opportunity.value.utilityTerritory || '',
  competitive_positioning: opportunity.value.competitivePositioning || '',
  risks_blockers_identified: opportunity.value.risksBlockersIdentified || '',
  recommended_next_steps: opportunity.value.recommendedNextSteps || '',
  ownership_status: opportunity.value.ownershipStatus || '',
  zoning_classification: opportunity.value.zoningClassification || '',
  usable_acreage_estimate: valueForInput(opportunity.value.usableAcreageEstimate),
  pad_acreage_estimate: valueForInput(opportunity.value.padAcreageEstimate),
  expansion_acreage_estimate: valueForInput(opportunity.value.expansionAcreageEstimate),
  gis_overlays_summary: opportunity.value.gisOverlaysSummary || '',
  existing_structures: opportunity.value.existingStructures || '',
  industrial_use_history: opportunity.value.industrialUseHistory || '',
  target_price: centsToDollars(opportunity.value.targetPriceCents),
  asking_price: centsToDollars(opportunity.value.askingPriceCents),
  next_follow_up_at: toLocalInputValue(opportunity.value.nextFollowUpAt),
  summary: opportunity.value.summary || '',
  parcel_apn: primaryParcel.value?.apn || '',
  parcel_address_line1: primaryParcel.value?.addressLine1 || '',
  parcel_city: primaryParcel.value?.city || '',
  parcel_acreage: valueForInput(primaryParcel.value?.acreage),
  parcel_latitude: valueForInput(primaryParcel.value?.latitude),
  parcel_longitude: valueForInput(primaryParcel.value?.longitude)
})

const startEditing = () => {
  editForm.value = defaultEditForm()
  formErrors.value = []
  errors.value = []
  isEditing.value = true
}

const cancelEditing = () => {
  if (isSaving.value) return

  formErrors.value = []
  isEditing.value = false
}

const saveOpportunity = async () => {
  isSaving.value = true
  formErrors.value = []
  errors.value = []

  const primaryParcelPayload = {
    apn: editForm.value.parcel_apn,
    address_line1: editForm.value.parcel_address_line1,
    city: editForm.value.parcel_city,
    county: editForm.value.county,
    state: editForm.value.state,
    acreage: editForm.value.parcel_acreage || editForm.value.acreage_estimate,
    latitude: editForm.value.parcel_latitude,
    longitude: editForm.value.parcel_longitude
  }
  const hasParcelData = Boolean(primaryParcel.value?.id) || Object.values(primaryParcelPayload).some((value) => String(value || '').trim() !== '')

  try {
    const data = await requestJson(`/opportunities/${opportunity.value.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        opportunity: {
          name: editForm.value.name,
          stage: editForm.value.stage,
          priority: editForm.value.priority,
          assignee_id: editForm.value.assignee_id,
          internal_identifier: editForm.value.internal_identifier,
          source: editForm.value.source,
          market: editForm.value.market,
          county: editForm.value.county,
          state: editForm.value.state,
          acreage_estimate: editForm.value.acreage_estimate,
          crm_fields_status: editForm.value.crm_fields_status,
          project_sponsor: editForm.value.project_sponsor,
          project_originator: editForm.value.project_originator,
          landowner_summary: editForm.value.landowner_summary,
          site_control_status: editForm.value.site_control_status,
          economics_summary: editForm.value.economics_summary,
          confidentiality_requirements: editForm.value.confidentiality_requirements,
          available_documents_summary: editForm.value.available_documents_summary,
          data_room_url: editForm.value.data_room_url,
          data_room_status: editForm.value.data_room_status,
          strategic_rationale: editForm.value.strategic_rationale,
          immediate_mw_opportunity: editForm.value.immediate_mw_opportunity,
          intermediate_mw_opportunity: editForm.value.intermediate_mw_opportunity,
          ultimate_mw_opportunity: editForm.value.ultimate_mw_opportunity,
          utility_territory: editForm.value.utility_territory,
          competitive_positioning: editForm.value.competitive_positioning,
          risks_blockers_identified: editForm.value.risks_blockers_identified,
          recommended_next_steps: editForm.value.recommended_next_steps,
          ownership_status: editForm.value.ownership_status,
          zoning_classification: editForm.value.zoning_classification,
          usable_acreage_estimate: editForm.value.usable_acreage_estimate,
          pad_acreage_estimate: editForm.value.pad_acreage_estimate,
          expansion_acreage_estimate: editForm.value.expansion_acreage_estimate,
          gis_overlays_summary: editForm.value.gis_overlays_summary,
          existing_structures: editForm.value.existing_structures,
          industrial_use_history: editForm.value.industrial_use_history,
          target_price_cents: dollarsToCents(editForm.value.target_price),
          asking_price_cents: dollarsToCents(editForm.value.asking_price),
          next_follow_up_at: editForm.value.next_follow_up_at,
          summary: editForm.value.summary,
          primary_parcel: hasParcelData ? primaryParcelPayload : undefined
        }
      })
    })

    detail.value = data.detail
    document.title = `${data.opportunity.name} | Landport`
    notice.value = `${data.opportunity.name} updated.`
    isEditing.value = false
  } catch (error) {
    formErrors.value = normalizeErrors(error)
  } finally {
    isSaving.value = false
  }
}

const updateStage = async (stage) => {
  if (opportunity.value.stage === stage) return

  errors.value = []

  try {
    const data = await requestJson(`/opportunities/${opportunity.value.id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage })
    })

    detail.value = {
      ...detail.value,
      opportunity: data.opportunity
    }
    if (isEditing.value) editForm.value.stage = data.opportunity.stage
    notice.value = `${data.opportunity.name} moved to ${data.opportunity.stageLabel}.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  }
}

const startDiligenceEdit = (item) => {
  editingDiligenceId.value = item.id
  diligenceForm.value = {
    title: item.title || '',
    status: item.status || 'not_started',
    due_on: item.dueOn || '',
    assignee_id: item.assigneeId || '',
    blocking: Boolean(item.blocking),
    description: item.description || ''
  }
  diligenceErrors.value = []
  errors.value = []
}

const cancelDiligenceEdit = () => {
  if (savingDiligenceId.value) return

  editingDiligenceId.value = null
  diligenceErrors.value = []
}

const saveDiligenceItem = async (item) => {
  savingDiligenceId.value = item.id
  diligenceErrors.value = []
  errors.value = []

  try {
    const data = await requestJson(`/diligence_items/${item.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        diligence_item: {
          title: diligenceForm.value.title,
          status: diligenceForm.value.status,
          due_on: diligenceForm.value.due_on,
          assignee_id: diligenceForm.value.assignee_id,
          blocking: diligenceForm.value.blocking,
          description: diligenceForm.value.description
        }
      })
    })

    detail.value = {
      ...detail.value,
      opportunity: data.opportunity,
      diligence: diligence.value.map((existingItem) => (
        existingItem.id === data.diligenceItem.id ? data.diligenceItem : existingItem
      ))
    }
    notice.value = `${data.diligenceItem.title} updated.`
    editingDiligenceId.value = null
  } catch (error) {
    diligenceErrors.value = normalizeErrors(error)
  } finally {
    savingDiligenceId.value = null
  }
}

const completeTask = async (task) => {
  try {
    const data = await requestJson(`/tasks/${task.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'completed' })
    })

    detail.value = {
      ...detail.value,
      opportunity: data.opportunity || opportunity.value,
      tasks: tasks.value.map((existingTask) => (
        existingTask.id === data.task.id ? data.task : existingTask
      ))
    }
    notice.value = `${data.task.title} marked ${data.task.statusLabel}.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  }
}

const syncOpportunity = async () => {
  isSyncing.value = true
  errors.value = []

  try {
    const data = await requestJson(`/clickup/opportunity_syncs/${opportunity.value.id}`, {
      method: 'POST'
    })

    detail.value = {
      ...detail.value,
      opportunity: data.opportunity
    }
    notice.value = `${data.opportunity.name} synced to ClickUp.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    isSyncing.value = false
  }
}

const toggleFavorite = async () => {
  isFavoriting.value = true
  errors.value = []

  try {
    const data = await requestJson(`/opportunities/${opportunity.value.id}/favorite`, {
      method: opportunity.value.favorited ? 'DELETE' : 'POST'
    })

    detail.value = {
      ...detail.value,
      opportunity: data.opportunity
    }
    notice.value = data.opportunity.favorited
      ? `${data.opportunity.name} added to favorites.`
      : `${data.opportunity.name} removed from favorites.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    isFavoriting.value = false
  }
}

const openScreeningDrawer = () => {
  showScreeningDrawer.value = true
  screeningErrors.value = []
}

const closeScreeningDrawer = () => {
  if (isRunningScreening.value) return

  showScreeningDrawer.value = false
}

const runScreening = async (keys = null) => {
  isRunningScreening.value = true
  screeningErrors.value = []
  errors.value = []

  try {
    const data = await requestJson(`/opportunities/${opportunity.value.id}/qualification_screening`, {
      method: 'POST',
      body: JSON.stringify({
        screening: {
          radius_miles: screeningRadius.value,
          keys: Array.isArray(keys) && keys.length > 0 ? keys : undefined
        }
      })
    })

    detail.value = data.detail
    notice.value = `Qualification screening completed for ${data.run?.summary?.total || activeScreeningFindings.value.length} checks.`
    showScreeningDrawer.value = true
  } catch (error) {
    screeningErrors.value = normalizeErrors(error)
    errors.value = screeningErrors.value
  } finally {
    isRunningScreening.value = false
  }
}

const applyScreeningFinding = async (finding) => {
  if (!canApplyScreeningFinding(finding)) return

  applyingFindingIds.value = [...applyingFindingIds.value, finding.id]
  screeningErrors.value = []
  errors.value = []

  try {
    const data = await requestJson(`/screening_findings/${finding.id}/apply`, {
      method: 'POST'
    })

    detail.value = data.detail
    notice.value = finding.diligenceItemId
      ? `${finding.title} applied to diligence.`
      : `${finding.title} added to diligence.`
  } catch (error) {
    screeningErrors.value = normalizeErrors(error)
    errors.value = screeningErrors.value
  } finally {
    applyingFindingIds.value = applyingFindingIds.value.filter((id) => id !== finding.id)
  }
}

const updateOpportunityImages = (updatedImages) => {
  detail.value = {
    ...detail.value,
    images: updatedImages
  }
}

const updateParcelImages = (parcelId, updatedImages) => {
  detail.value = {
    ...detail.value,
    parcels: parcels.value.map((parcel) => (
      parcel.id === parcelId ? { ...parcel, images: updatedImages } : parcel
    ))
  }
}

const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0))
const formatOptionalNumber = (value) => {
  if (value === null || value === undefined || value === '') return 'Not set'

  return new Intl.NumberFormat().format(Number(value))
}
const formatAcres = (value) => {
  const formatted = formatOptionalNumber(value)

  return formatted === 'Not set' ? formatted : `${formatted} acres`
}
const formatMw = (value) => {
  const formatted = formatOptionalNumber(value)

  return formatted === 'Not set' ? formatted : `${formatted} MW`
}
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined || cents === '') return 'Not set'

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(Number(cents) / 100)
}

const formatDateTime = (value) => {
  if (!value) return 'Never'

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value))
}

const valueForInput = (value) => (value === null || value === undefined ? '' : String(value))
const centsToDollars = (cents) => (cents === null || cents === undefined || cents === '' ? '' : (Number(cents) / 100).toFixed(2))
const dollarsToCents = (value) => {
  if (value === null || value === undefined || value === '') return ''

  return Math.round(Number(value) * 100)
}

const toLocalInputValue = (value) => {
  if (!value) return ''

  const date = new Date(value)
  const pad = (number) => String(number).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const progressTone = (record) => {
  if (Number(record.diligenceSummary?.blocked || 0) > 0 || Number(record.diligenceSummary?.failed || 0) > 0) return 'red'
  if (Number(record.diligenceSummary?.overdue || 0) > 0 || Number(record.diligenceSummary?.dueSoon || 0) > 0) return 'amber'
  if (Number(record.diligenceProgress || 0) === 100) return 'green'

  return 'blue'
}
const priorityTone = (priority) => ({
  low: 'slate',
  normal: 'blue',
  high: 'amber',
  urgent: 'red'
})[priority] || 'slate'
const itemToneBorder = (item) => {
  const tone = item.blocking || item.statusTone === 'red' || item.overdue
    ? 'red'
    : item.dueSoon
      ? 'amber'
      : item.dueTone || item.statusTone || item.priorityTone

  return ({
    red: 'border-l-red-500',
    amber: 'border-l-amber-500',
    green: 'border-l-emerald-500',
    blue: 'border-l-blue-500',
    violet: 'border-l-violet-500',
    slate: 'border-l-transparent'
  })[tone] || 'border-l-transparent'
}
const screeningFindingRank = (finding) => ({
  blocked: 0,
  failed: 1,
  review_required: 2,
  unavailable: 3,
  available: 4,
  passed: 5,
  running: 6,
  not_run: 7
})[finding.status] ?? 8
const isApplyingFinding = (id) => applyingFindingIds.value.includes(id)
const canApplyScreeningFinding = (finding) => Boolean(finding.id) && finding.status !== 'not_run'
const screeningActionLabel = (finding) => {
  if (!finding.id) return 'Run first'
  if (finding.diligenceItemId) return finding.reviewedAt ? 'Update diligence' : 'Apply to diligence'

  return 'Create diligence item'
}
const screeningActionDetail = (finding) => {
  if (!finding.id) return 'This question needs a screening result before it can become diligence work.'
  if (finding.diligenceItemId) {
    return finding.reviewedAt
      ? 'Refreshes the linked diligence item with the latest status, summary, and recommendation.'
      : 'Pushes this finding into the linked diligence item and marks it for active review.'
  }

  return 'Creates a tracked diligence item for this screening question, links it to the finding, and records the recommendation.'
}
const screeningQuestion = (finding) => ({
  'screening.desktop_map_review': 'Do the mapped parcels and overlays reveal immediate site, infrastructure, or risk constraints?',
  'screening.regional_market': 'Does the local market context support advancing this site for powered land demand?',
  'screening.market_economics': 'What do public construction and market-momentum signals imply for this county?',
  'screening.nearby_data_centers': 'Which operational data center operators nearby validate the market or compete for constrained infrastructure?',
  'screening.transmission_adjacency': 'Is the site meaningfully close to public transmission and substation infrastructure?',
  'screening.renewable_resource': 'Does the site have meaningful renewable-resource optionality for power-cost or carbon strategy?',
  'screening.distribution_adjacency': 'What utility distribution evidence is still required to understand service path and capacity?',
  'screening.fiber_adjacency': 'What public or paid evidence supports carrier-grade fiber availability near the site?',
  'screening.water_wastewater': 'What water, wastewater, and stormwater capacity questions remain before qualification?',
  'screening.natural_gas': 'Is public pipeline infrastructure close enough to justify LNG or gas-service follow-up?',
  'screening.terrain_buildability': 'Does public elevation data reveal slope or relief concerns that could reduce usable acreage?',
  'screening.land_cover': 'What does public land-cover data suggest about current site use and conversion constraints?',
  'screening.transportation_access': 'Can the site support construction, equipment movement, and ongoing access requirements?',
  'screening.workforce': 'Does the nearby workforce profile support development, construction, and operations?',
  'screening.community_receptiveness': 'Are public signals likely to help or complicate local approvals and community acceptance?',
  'screening.political_climate': 'Does the local political environment appear supportive of this type of industrial load?'
})[finding.key] || 'What does this screening result imply for qualification?'
const screeningDecisionSignal = (finding) => ({
  blocked: 'Stop and resolve before advancing.',
  failed: 'Automation failed; rerun or inspect the source before relying on this result.',
  unavailable: 'Required source data is missing or unavailable.',
  review_required: 'Evidence exists, but a human decision is required.',
  available: 'Evidence is available and should be preserved for diligence.',
  passed: 'This question has been cleared.',
  not_run: 'No decision signal yet.'
})[finding.status] || 'Review this finding before using it in a stage decision.'
const sourceStatusCount = (finding, status) => (finding.sources || []).filter((source) => source.status === status).length
const sourceCoverageTone = (finding) => {
  if (sourceStatusCount(finding, 'failed') > 0 || sourceStatusCount(finding, 'unavailable') > 0) return 'red'
  if (sourceStatusCount(finding, 'available') > 0) return 'blue'
  if (sourceStatusCount(finding, 'planned') > 0 || sourceStatusCount(finding, 'manual_required') > 0 || sourceStatusCount(finding, 'recommended') > 0) return 'amber'

  return 'slate'
}
const metricEntries = (metrics) => flattenMetricEntries(metrics)
  .filter((entry) => entry.value !== null && entry.value !== undefined && entry.value !== '')
  .slice(0, 6)
  .map((entry) => ({
    key: entry.key,
    label: titleize(entry.key),
    value: formatMetricValue(entry.value)
  }))
const flattenMetricEntries = (metrics, prefix = '') => Object.entries(metrics || {}).flatMap(([key, value]) => {
  const entryKey = prefix ? `${prefix} ${key}` : key
  if (value === null || value === undefined) return []
  if (Array.isArray(value)) return [{ key: entryKey, value: value.length }]
  if (typeof value === 'object') {
    if (value.name && value.distanceMiles !== undefined) {
      return [
        { key: `${entryKey} name`, value: value.name },
        { key: `${entryKey} distance miles`, value: value.distanceMiles }
      ]
    }

    return flattenMetricEntries(value, entryKey)
  }

  return [{ key: entryKey, value }]
})
const titleize = (value) => String(value || '')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())
const formatMetricValue = (value) => {
  if (typeof value === 'number') return Number.isInteger(value) ? formatNumber(value) : value.toFixed(2)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  return String(value)
}
</script>
